import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { api, formatErr } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { BadgeCheck, MapPin, Star, MessageCircle, CalendarPlus, Globe, ShieldCheck, Users, UserCheck, LayoutList, DollarSign, X, Loader2 } from "lucide-react";
import { PAYMENT_MAP, SAFE_METHODS, PLATFORM_FEE_RATE } from "@/lib/payments";
import FollowButton from "@/components/FollowButton";
import ShareProfileButton from "@/components/ShareProfileButton";
import FollowersModal from "@/components/FollowersModal";
import ReviewCard from "@/components/ReviewCard";
import { VideoReelView } from "@/components/VideoReel";
import TranslatedText from "@/components/TranslatedText";

export default function Profile() {
  const { id } = useParams();
  const { user } = useAuth();
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [booking, setBooking] = useState({ svcId: "", date: "", notes: "", payment_method: "" });
  const [bookErr, setBookErr] = useState("");
  const [bookOk, setBookOk] = useState("");
  const [bookBusy, setBookBusy] = useState(false);
  const [guestBooking, setGuestBooking] = useState(false);   // "true" opens a guest modal
  const [reviewText, setReviewText] = useState("");
  const [reviewRating, setReviewRating] = useState(5);
  const [networkModal, setNetworkModal] = useState(null); // null | "followers" | "following"
  const [tipOpen, setTipOpen] = useState(false);

  const load = () => api.get(`/users/${id}`).then(r => setData(r.data));
  useEffect(()=>{ load(); }, [id]);

  if (!data) return <div className="p-10 text-center text-[#8A8A8A]" data-testid="profile-loading">Loading…</div>;
  const { user: u, services, products = [], posts, reviews } = data;
  const isOwn = user?.id === u.id;

  const book = async (e) => {
    e.preventDefault();
    setBookErr(""); setBookOk("");
    if (!booking.svcId || !booking.date) { setBookErr("Pick a service and a date."); return; }
    // Guests get an inline modal for email + name so we can email them the payment link.
    if (!user) { setGuestBooking(true); return; }
    const method = booking.payment_method || (u.payment_methods?.[0] || "card");
    setBookBusy(true);
    try {
      const { data: b } = await api.post("/bookings", {
        hooman_id: u.id, service_id: booking.svcId, date: booking.date, notes: booking.notes,
        payment_method: method,
      });
      nav(`/checkout/${b.id}`);
    } catch (e) { setBookErr(formatErr(e)); setBookBusy(false); }
  };

  const bookAsGuest = async ({ email, name }) => {
    setBookErr(""); setBookBusy(true);
    const method = booking.payment_method || (u.payment_methods?.[0] || "card");
    try {
      const { data: b } = await api.post("/bookings", {
        hooman_id: u.id, service_id: booking.svcId, date: booking.date, notes: booking.notes,
        payment_method: method, guest_email: email, guest_name: name,
      });
      // Hand the guest token to /checkout so the payment step doesn't need a login.
      nav(`/checkout/${b.id}?t=${b.guest_token}`);
    } catch (e) { setBookErr(formatErr(e)); setBookBusy(false); setGuestBooking(false); }
  };

  const message = async () => {
    if (!user) { nav("/login"); return; }
    nav(`/messages/${u.id}`);
  };

  const submitReview = async (e) => {
    e.preventDefault();
    if (!user) { nav("/login"); return; }
    await api.post("/reviews", { hooman_id: u.id, rating: reviewRating, text: reviewText });
    setReviewText(""); setReviewRating(5);
    load();
  };

  const method = booking.payment_method || u.payment_methods?.[0] || "card";
  const selectedSvc = services.find(s => s.id === booking.svcId);
  const grossPrice = selectedSvc?.price || 0;
  const platformFee = Math.round(grossPrice * PLATFORM_FEE_RATE * 100) / 100;
  const isSafeMethod = SAFE_METHODS.has(method);

  return (
    <div className="bg-[#FAFAFA]">
      <div className="relative h-72 sm:h-96 overflow-hidden">
        {u.cover && <img src={u.cover} alt="" className="absolute inset-0 w-full h-full object-cover" style={{ objectPosition: u.cover_position || "50% 50%" }} />}
        <div className="absolute inset-0 bg-gradient-to-t from-[#FAFAFA] via-transparent to-transparent" />
      </div>

      <div className="max-w-7xl mx-auto px-6 sm:px-8 -mt-16 relative">
        <div className="flex items-end gap-6 flex-wrap">
          <img src={u.avatar} alt={u.name} className="h-32 w-32 rounded-full border-4 border-white object-cover shadow-lg" style={{ objectPosition: u.avatar_position || "50% 50%" }} data-testid="profile-avatar" />
          <div className="flex-1 min-w-[220px]">
            <div className="flex items-center gap-3 flex-wrap">
              <h1 className="h-display text-4xl" data-testid="profile-name">{u.name}</h1>
              {u.is_verified && <span className="badge badge-verified"><BadgeCheck size={12}/> Verified Human</span>}
            </div>
            <p className="overline mt-2 text-[#0057FF]">{(u.categories?.length ? u.categories : [u.category]).filter(Boolean).join(" · ")}</p>
            <div className="flex flex-wrap gap-4 mt-3 text-sm text-[#4A4A4A]">
              <span className="inline-flex items-center gap-1.5"><MapPin size={14}/> {u.location || "—"}</span>
              <span className="inline-flex items-center gap-1.5"><Star size={14} className="fill-[#0057FF] stroke-[#0057FF]"/> {u.trust_score} trust</span>
              {u.languages?.length>0 && <span className="inline-flex items-center gap-1.5"><Globe size={14}/> {u.languages.join(", ")}</span>}
              <span>{u.years_experience} yrs experience</span>
            </div>

            {/* Clickable network counts */}
            <div className="flex items-center gap-5 mt-3 text-sm" data-testid="profile-network">
              <button
                type="button"
                onClick={() => setNetworkModal("followers")}
                className="group inline-flex items-center gap-1.5 text-[#0A0A0A] hover:text-[#0057FF] transition-colors"
                data-testid="open-followers"
              >
                <Users size={14}/>
                <span className="font-semibold">{u.follower_count ?? 0}</span>
                <span className="text-[#8A8A8A] group-hover:text-[#0057FF]">followers</span>
              </button>
              <span className="text-[#E5E5E3]">·</span>
              <button
                type="button"
                onClick={() => setNetworkModal("following")}
                className="group inline-flex items-center gap-1.5 text-[#0A0A0A] hover:text-[#0057FF] transition-colors"
                data-testid="open-following"
              >
                <UserCheck size={14}/>
                <span className="font-semibold">{u.following_count ?? 0}</span>
                <span className="text-[#8A8A8A] group-hover:text-[#0057FF]">following</span>
              </button>
            </div>
          </div>
          <div className="flex gap-2 flex-wrap">
            {isOwn ? (
              <>
                <FollowButton target={u} />
                <ShareProfileButton user={u} />
                <Link to={`/u/${u.id}/listings`} className="btn-secondary" data-testid="profile-listings-btn"><LayoutList size={16}/> Listings</Link>
                <Link to="/profile/edit" className="btn-secondary" data-testid="profile-edit-btn">Edit profile</Link>
              </>
            ) : (
              <>
                <FollowButton target={u} />
                <ShareProfileButton user={u} />
                <Link to={`/u/${u.id}/listings`} className="btn-secondary" data-testid="profile-listings-btn"><LayoutList size={16}/> Listings</Link>
                <button onClick={message} className="btn-secondary" data-testid="profile-message-hooman"><MessageCircle size={16}/> Message</button>
              </>
            )}
          </div>
        </div>

        <div className="mt-10 flex flex-col lg:grid lg:grid-cols-3 gap-8">
          {/* LEFT — on mobile the wrapper is "contents" so its children become
              direct flex siblings, letting us reorder About/Work/Shop/Reels/
              Reviews relative to the RIGHT booking aside with order-* classes.
              Desktop keeps the classic 2-col layout via lg:*. */}
          <div className="contents lg:col-span-2 lg:block lg:space-y-8">
            <div className="card p-8 order-1 lg:order-none">
              <p className="overline mb-3">About</p>
              <TranslatedText text={u.bio || ""} className="text-lg text-[#0A0A0A] leading-relaxed" testid="profile-bio"/>
              {u.skills?.length>0 && (
                <div className="flex flex-wrap gap-2 mt-6">
                  {u.skills.map((s,i)=>(<span key={i} className="badge-cat">{s}</span>))}
                </div>
              )}
            </div>

            {posts.length>0 && (
              <div className="order-2 lg:order-none">
                <p className="overline mb-4">Work</p>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3" data-testid="profile-portfolio">
                  {posts.map(p => (
                    <div key={p.id} className="aspect-square overflow-hidden rounded-2xl bg-[#F2F2F0]">
                      <img src={p.image} alt="" className="w-full h-full object-cover hover:scale-105 transition-transform duration-500"/>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {products.length > 0 && (
              <div className="order-3 lg:order-none" data-testid="profile-products">
                <p className="overline mb-4">Shop · {products.length}</p>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                  {products.map(p => (
                    <Link key={p.id} to={`/shop/${p.id}`} className="card overflow-hidden block group" data-testid={`profile-product-${p.id}`}>
                      <div className="aspect-square bg-[#F2F2F0]">
                        {p.image && <img src={p.image} alt="" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"/>}
                      </div>
                      <div className="p-3">
                        <p className="font-medium text-sm line-clamp-1">{p.title}</p>
                        <p className="text-xs text-[#4A4A4A] mt-1 flex justify-between">
                          <span>{p.product_type}</span><span className="font-heading">${p.price}</span>
                        </p>
                      </div>
                    </Link>
                  ))}
                </div>
              </div>
            )}

            {u.videos?.length > 0 && (
              <div className="mt-2 order-4 lg:order-none" data-testid="profile-reel">
                <VideoReelView videos={u.videos}/>
              </div>
            )}

            {!isOwn && (
              <div className="order-6 lg:order-none" data-testid="profile-send-money">
                <div className="card p-6">
                  <div className="flex items-center gap-3">
                    <div className="h-10 w-10 rounded-full bg-[#0057FF] text-white inline-flex items-center justify-center">
                      <DollarSign size={18}/>
                    </div>
                    <div className="flex-1">
                      <p className="font-heading text-lg leading-tight">Send {u.name?.split(" ")[0]} some love</p>
                      <p className="text-sm text-[#4A4A4A]">Tip, gift, or say thanks with any amount.</p>
                    </div>
                  </div>
                  <button
                    onClick={()=>setTipOpen(true)}
                    className="btn-primary w-full mt-4"
                    data-testid="profile-send-money-btn">
                    <DollarSign size={14}/> Send money
                  </button>
                  <p className="text-[11px] text-[#8A8A8A] text-center mt-2">Card / Apple Pay / Google Pay via Stripe · 2 % Hoomanz Tip added on top.</p>
                </div>
              </div>
            )}

            <div className="order-7 lg:order-none">
              <p className="overline mb-4">Reviews · {reviews.length}</p>
              <div className="space-y-4" data-testid="profile-reviews">
                {reviews.map((r) => (
                  <ReviewCard
                    key={r.id}
                    review={{ ...r, hooman: { name: u.name } }}
                    isRecipient={isOwn}
                    onChange={load}
                  />
                ))}
                {reviews.length === 0 && <p className="text-[#8A8A8A] text-sm">No reviews yet — be the first.</p>}
              </div>
              {!isOwn && user && (
                <form onSubmit={submitReview} className="card p-6 mt-4" data-testid="review-form">
                  <p className="overline mb-3">Leave a review</p>
                  <div className="flex gap-1 mb-3">
                    {[1,2,3,4,5].map(n=>(
                      <button type="button" key={n} onClick={()=>setReviewRating(n)} data-testid={`review-star-${n}`}>
                        <Star size={22} className={n<=reviewRating?"fill-[#0057FF] stroke-[#0057FF]":"stroke-[#8A8A8A]"}/>
                      </button>
                    ))}
                  </div>
                  <textarea required value={reviewText} onChange={(e)=>setReviewText(e.target.value)}
                    className="input" rows={3} placeholder="Share your experience" data-testid="review-text"/>
                  <button className="btn-primary mt-3" data-testid="review-submit">Post review</button>
                </form>
              )}
            </div>
          </div>

          {/* RIGHT — Services + Booking. On mobile it slots between Reels (order-4)
              and Reviews (order-6). */}
          <aside className="space-y-5 order-5 lg:order-none">
            <div className="card p-7 sticky top-24">
              <p className="overline mb-4">Book {u.name?.split(" ")[0]}</p>
              {services.length === 0 ? (
                <p className="text-sm text-[#8A8A8A]">No services listed yet.</p>
              ) : (
                <form onSubmit={book} data-testid="booking-form">
                  <div className="space-y-2">
                    {services.map((s) => (
                      <label key={s.id} className={`block border rounded-2xl p-4 cursor-pointer transition-all ${booking.svcId===s.id ? "border-[#0A0A0A] bg-[#F2F2F0]":"border-[#E5E5E3] hover:bg-[#F2F2F0]"}`}>
                        <input type="radio" name="svc" className="hidden" checked={booking.svcId===s.id}
                          onChange={()=>setBooking({...booking, svcId:s.id})} data-testid={`booking-service-${s.id}`} />
                        <div className="flex gap-3">
                          {s.image && <img src={s.image} alt="" className="h-16 w-16 rounded-xl object-cover flex-shrink-0"/>}
                          <div className="flex-1 min-w-0">
                            <div className="flex items-baseline justify-between gap-3">
                              <p className="font-medium">{s.title}</p>
                              <p className="font-heading text-lg">${s.price}</p>
                            </div>
                            <p className="text-sm text-[#4A4A4A] mt-1">{s.description}</p>
                            <p className="text-xs text-[#8A8A8A] mt-1">{s.duration_min} min</p>
                          </div>
                        </div>
                      </label>
                    ))}
                  </div>
                  <input type="datetime-local" required value={booking.date}
                    onChange={(e)=>setBooking({...booking, date:e.target.value})}
                    className="input mt-4" data-testid="booking-date" />

                  {u.payment_methods?.length > 0 && (
                    <div className="mt-4" data-testid="booking-payment-methods">
                      <p className="text-xs text-[#4A4A4A] mb-2">How will you pay?</p>
                      <div className="flex gap-2 flex-wrap">
                        {u.payment_methods.map((pmId) => {
                          const pm = PAYMENT_MAP[pmId];
                          if (!pm) return null;
                          const Icon = pm.Icon;
                          const on = (booking.payment_method || u.payment_methods[0]) === pmId;
                          return (
                            <button type="button" key={pmId}
                              onClick={()=>setBooking({...booking, payment_method: pmId})}
                              data-testid={`booking-pm-${pmId}`}
                              className={`inline-flex items-center gap-1.5 px-3 py-2 rounded-full text-sm border transition-all
                                ${on ? "bg-[#0A0A0A] text-white border-[#0A0A0A]" : "bg-white text-[#0A0A0A] border-[#E5E5E3] hover:bg-[#F2F2F0]"}`}>
                              <Icon size={14}/> {pm.label}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  )}
                  <textarea placeholder="Anything they should know? (optional)" value={booking.notes}
                    onChange={(e)=>setBooking({...booking, notes:e.target.value})}
                    rows={2} className="input mt-3" data-testid="booking-notes" />
                  {selectedSvc && (
                    <div className="mt-4 p-4 rounded-2xl bg-[#F2F2F0] text-sm" data-testid="booking-breakdown">
                      <div className="flex justify-between" data-testid="booking-subtotal"><span>Subtotal</span><span>${grossPrice.toFixed(2)}</span></div>
                      <div className="flex justify-between text-[#4A4A4A] text-xs mt-1.5" data-testid="booking-fee">
                        <span>Hoomanz Tip (2%)</span><span>+${platformFee.toFixed(2)}</span>
                      </div>
                      <div className="flex justify-between font-heading text-base mt-2 pt-2 border-t border-[#E5E5E3]" data-testid="booking-total"><span>Total</span><span>${(grossPrice + platformFee).toFixed(2)}</span></div>
                      {!isSafeMethod && (
                        <p className="mt-3 inline-flex items-center gap-1.5 text-[#0044CC] text-xs"><ShieldCheck size={12}/> Pay {method.replace('_',' ')} directly with your Hooman — not protected by Hoomanz.</p>
                      )}
                    </div>
                  )}
                  {bookErr && <p className="text-sm text-[#B84B4B] mt-3">{bookErr}</p>}
                  {bookOk && <p className="text-sm text-[#0A0A0A] mt-3" data-testid="booking-success">{bookOk}</p>}
                  <button className="btn-primary w-full mt-4" disabled={!booking.svcId || !booking.date || bookBusy} data-testid="booking-confirm-btn">
                    <CalendarPlus size={16}/> {bookBusy ? "Opening checkout…" : "Continue to checkout"}
                  </button>
                  <p className="text-xs text-[#8A8A8A] text-center mt-3">Card / Apple Pay / Google Pay route through Stripe. Cash / Bank / Crypto are settled with your Hooman directly.</p>
                </form>
              )}
            </div>
          </aside>
        </div>
      </div>
      <div className="h-24"/>

      {networkModal && (
        <FollowersModal
          userId={u.id}
          userName={u.name}
          initialTab={networkModal}
          onClose={() => { setNetworkModal(null); load(); }}
        />
      )}

      {guestBooking && (
        <GuestBookingModal
          hoomanName={u.name}
          busy={bookBusy}
          err={bookErr}
          onClose={()=>{ setGuestBooking(false); setBookErr(""); }}
          onSubmit={bookAsGuest}
        />
      )}

      {tipOpen && (
        <TipModal
          recipient={u}
          currentUser={user}
          onClose={()=>setTipOpen(false)}
        />
      )}
    </div>
  );
}

function TipModal({ recipient, currentUser, onClose }) {
  const [amount, setAmount] = useState(10);
  const [message, setMessage] = useState("");
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const platformFee = Math.round((Number(amount) || 0) * PLATFORM_FEE_RATE * 100) / 100;

  const PRESETS = [5, 10, 25, 50];

  const submit = async (e) => {
    e.preventDefault();
    const amt = Number(amount);
    if (!amt || amt < 1) { setErr("Enter an amount of at least $1"); return; }
    if (!currentUser) {
      const em = email.trim();
      if (!em || !/^\S+@\S+\.\S+$/.test(em)) { setErr("Enter a valid email so we can send your receipt."); return; }
    }
    setBusy(true); setErr("");
    try {
      const body = {
        recipient_id: recipient.id,
        amount: amt,
        origin_url: window.location.origin,
        message: message.trim(),
      };
      if (!currentUser) { body.guest_email = email.trim(); body.guest_name = name.trim(); }
      const { data } = await api.post("/tips", body);
      window.location.href = data.url;
    } catch (e) { setErr(formatErr(e)); setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm flex items-center justify-center p-4"
         onClick={onClose} data-testid="tip-modal">
      <form onSubmit={submit} onClick={(e)=>e.stopPropagation()}
            className="bg-white rounded-3xl p-6 max-w-sm w-full">
        <div className="flex items-center justify-between mb-3">
          <p className="overline">Send money</p>
          <button type="button" onClick={onClose} aria-label="Close" data-testid="tip-modal-close"><X size={16}/></button>
        </div>
        <h3 className="font-heading text-2xl leading-tight">Send {recipient.name?.split(" ")[0]} a tip</h3>
        <p className="text-sm text-[#4A4A4A] mt-1 mb-4">
          Any amount. Card / Apple Pay / Google Pay via Stripe. A 2 % Hoomanz Tip is added on top — they get 100 %.
        </p>

        <div className="flex flex-wrap gap-2 mb-3" data-testid="tip-presets">
          {PRESETS.map(v => (
            <button type="button" key={v} onClick={()=>setAmount(v)}
              className={`px-3 py-1.5 rounded-full text-sm border transition-all
                ${Number(amount)===v ? "bg-[#0A0A0A] text-white border-[#0A0A0A]" : "bg-white text-[#0A0A0A] border-[#E5E5E3] hover:bg-[#F2F2F0]"}`}
              data-testid={`tip-preset-${v}`}>
              ${v}
            </button>
          ))}
        </div>

        <label className="block">
          <span className="text-xs uppercase tracking-wider text-[#4A4A4A]">Amount (USD)</span>
          <input type="number" min="1" step="1" required value={amount}
            onChange={(e)=>setAmount(e.target.value)} className="input mt-1.5"
            data-testid="tip-amount"/>
        </label>

        <label className="block mt-3">
          <span className="text-xs uppercase tracking-wider text-[#4A4A4A]">Message (optional)</span>
          <textarea rows={2} value={message} onChange={(e)=>setMessage(e.target.value)}
            className="input mt-1.5" maxLength={500}
            placeholder="Loved your last piece — keep going." data-testid="tip-message"/>
        </label>

        {!currentUser && (
          <>
            <label className="block mt-3">
              <span className="text-xs uppercase tracking-wider text-[#4A4A4A]">Your name</span>
              <input value={name} onChange={(e)=>setName(e.target.value)}
                className="input mt-1.5" data-testid="tip-guest-name"/>
            </label>
            <label className="block mt-3">
              <span className="text-xs uppercase tracking-wider text-[#4A4A4A]">Your email</span>
              <input type="email" required value={email} onChange={(e)=>setEmail(e.target.value)}
                className="input mt-1.5" placeholder="you@example.com" data-testid="tip-guest-email"/>
            </label>
          </>
        )}

        <div className="mt-4 p-3 rounded-xl bg-[#F2F2F0] text-sm" data-testid="tip-breakdown">
          <div className="flex justify-between" data-testid="tip-subtotal"><span>Subtotal</span><span>${Number(amount || 0).toFixed(2)}</span></div>
          <div className="flex justify-between text-[#4A4A4A] text-xs mt-1" data-testid="tip-fee"><span>Hoomanz Tip (2 %)</span><span>+${platformFee.toFixed(2)}</span></div>
          <div className="flex justify-between font-heading text-base mt-1.5 pt-1.5 border-t border-[#E5E5E3]" data-testid="tip-total">
            <span>You pay</span>
            <span>${(Number(amount || 0) + platformFee).toFixed(2)}</span>
          </div>
          <p className="text-[11px] text-[#8A8A8A] mt-1.5">{recipient.name?.split(" ")[0]} receives ${Number(amount || 0).toFixed(2)} (100 %).</p>
        </div>

        {err && <p className="text-sm text-[#B84B4B] mt-3" data-testid="tip-err">{err}</p>}
        <div className="flex gap-2 mt-5">
          <button type="button" onClick={onClose} className="btn-ghost flex-1" data-testid="tip-cancel">Cancel</button>
          <button type="submit" disabled={busy} className="btn-primary flex-1" data-testid="tip-submit">
            {busy ? <><Loader2 size={14} className="animate-spin"/> Opening…</> : <><DollarSign size={14}/> Send ${(Number(amount||0) + platformFee).toFixed(2)}</>}
          </button>
        </div>
        {!currentUser && (
          <p className="text-[11px] text-[#8A8A8A] mt-3 text-center">
            Already have an account? <Link to="/login" className="underline">Log in</Link> to keep a record.
          </p>
        )}
      </form>
    </div>
  );
}

function GuestBookingModal({ hoomanName, busy, err, onClose, onSubmit }) {
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const submit = (e) => {
    e.preventDefault();
    const em = email.trim();
    if (!em || !/^\S+@\S+\.\S+$/.test(em)) return;
    onSubmit({ email: em, name: name.trim() });
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm flex items-center justify-center p-4"
         onClick={onClose} data-testid="guest-booking-modal">
      <form onSubmit={submit} onClick={(e)=>e.stopPropagation()}
            className="bg-white rounded-3xl p-6 max-w-sm w-full">
        <p className="overline mb-1">Guest booking</p>
        <h3 className="font-heading text-2xl">Book {hoomanName?.split(" ")[0]} without an account</h3>
        <p className="text-sm text-[#4A4A4A] mt-1 mb-4">
          We&apos;ll email you the payment link + booking receipt, and (for online calls) your join link.
          No password required.
        </p>
        <label className="block">
          <span className="text-xs uppercase tracking-wider text-[#4A4A4A]">Your name</span>
          <input value={name} onChange={(e)=>setName(e.target.value)}
                 placeholder="Alex Rivera" className="input mt-1.5" autoFocus
                 data-testid="guest-booking-name"/>
        </label>
        <label className="block mt-3">
          <span className="text-xs uppercase tracking-wider text-[#4A4A4A]">Your email</span>
          <input type="email" required value={email} onChange={(e)=>setEmail(e.target.value)}
                 placeholder="you@example.com" className="input mt-1.5"
                 data-testid="guest-booking-email"/>
        </label>
        {err && <p className="text-sm text-[#B84B4B] mt-3" data-testid="guest-booking-err">{err}</p>}
        <div className="flex gap-2 mt-5">
          <button type="button" onClick={onClose} className="btn-ghost flex-1"
                  data-testid="guest-booking-cancel">Cancel</button>
          <button type="submit" disabled={busy} className="btn-primary flex-1"
                  data-testid="guest-booking-continue">
            {busy ? "Opening…" : "Continue to payment"}
          </button>
        </div>
        <p className="text-[11px] text-[#8A8A8A] mt-3 text-center">
          Already have an account? <Link to="/login" className="underline">Log in instead</Link>.
        </p>
      </form>
    </div>
  );
}
